"use client"

import { useState, useEffect } from "react"
import { useAuth0 } from '@auth0/auth0-react';
import { getApiBaseUrl } from "../config/apiBase"

const useTimeline = (actionId, action) => {
  console.log("useTimeline hook initialized with actionId:", actionId)

  const [timeline, setTimeline] = useState({
    startDate: "",
    endDate: "",
    milestones: [],
  })
  const [suggestions, setSuggestions] = useState([])
  const [comments, setComments] = useState([])
  const [newSuggestion, setNewSuggestion] = useState({
    startDate: "",
    endDate: "",
    milestones: [{ title: "", date: "", description: "" }],
  })
  const [newComment, setNewComment] = useState("")
  const [activeTab, setActiveTab] = useState("current")
  const [loading, setLoading] = useState(true)
  const { getAccessTokenSilently } = useAuth0();

  const apiUrl = getApiBaseUrl()

  console.log("API URL:", apiUrl)

  const fetchTimelineData = async () => {
    console.log("fetchTimelineData called with actionId:", actionId)

    if (!actionId) {
      console.log("No actionId provided, skipping fetch")
      setLoading(false)
      return
    }

    try {
      setLoading(true)
        const token = await getAccessTokenSilently({
            authorizationParams: {
                audience: process.env.REACT_APP_API_AUDIENCE,
            }
        });
      console.log("Token exists:", !!token)

      // Fetch the current timeline
      const timelineUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/timeline`
      console.log("Fetching timeline from:", timelineUrl)

      const timelineResponse = await fetch(timelineUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Timeline response status:", timelineResponse.status)

      if (timelineResponse.ok) {
        const timelineData = await timelineResponse.json()
        console.log("Timeline data received:", timelineData)
        setTimeline(
          timelineData || {
            startDate: action?.executionDate || "",
            endDate: "",
            milestones: [],
          },
        )
      } else {
        console.error("Timeline response not ok:", timelineResponse.status, timelineResponse.statusText)
      }

      // Fetch suggestions
      const suggestionsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/timeline/suggestions`
      console.log("Fetching suggestions from:", suggestionsUrl)

      const suggestionsResponse = await fetch(suggestionsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Suggestions response status:", suggestionsResponse.status)

      if (suggestionsResponse.ok) {
        const suggestionsData = await suggestionsResponse.json()
        console.log("Suggestions data received:", suggestionsData)
        setSuggestions(suggestionsData || [])
      } else {
        console.error("Suggestions response not ok:", suggestionsResponse.status, suggestionsResponse.statusText)
      }

      // Fetch comments
      const commentsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/timeline/comments`
      console.log("Fetching comments from:", commentsUrl)

      const commentsResponse = await fetch(commentsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Comments response status:", commentsResponse.status)

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json()
        console.log("Comments data received:", commentsData)
        setComments(commentsData || [])
      } else {
        console.error("Comments response not ok:", commentsResponse.status, commentsResponse.statusText)
      }
    } catch (error) {
      console.error("Error fetching timeline data:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddMilestone = () => {
    setNewSuggestion((prev) => ({
      ...prev,
      milestones: [...prev.milestones, { title: "", date: "", description: "" }],
    }))
  }

  const handleRemoveMilestone = (index) => {
    setNewSuggestion((prev) => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index),
    }))
  }

  const handleMilestoneChange = (index, field, value) => {
    setNewSuggestion((prev) => {
      const updatedMilestones = [...prev.milestones]
      updatedMilestones[index] = {
        ...updatedMilestones[index],
        [field]: value,
      }
      return {
        ...prev,
        milestones: updatedMilestones,
      }
    })
  }

  const updateNewSuggestion = (field, value) => {
    setNewSuggestion((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmitSuggestion = async () => {
    console.log("handleSubmitSuggestion called with:", newSuggestion)

    if (!newSuggestion.startDate || !newSuggestion.endDate) {
      console.log("Missing required dates, skipping submit")
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/modules/timeline/suggestions`

      console.log("Submitting suggestion to:", url)
      console.log("Suggestion data:", newSuggestion)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: JSON.stringify(newSuggestion) }),
      })

      console.log("Submit suggestion response status:", response.status)

      if (response.ok) {
        setNewSuggestion({
          startDate: "",
          endDate: "",
          milestones: [{ title: "", date: "", description: "" }],
        })
        await fetchTimelineData()
      } else {
        console.error("Failed to submit suggestion:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting suggestion:", error)
    }
  }

  const handleSubmitComment = async () => {
    console.log("handleSubmitComment called with:", newComment)

    if (!newComment.trim()) {
      console.log("Empty comment, skipping submit")
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/modules/timeline/comments`

      console.log("Submitting comment to:", url)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newComment }),
      })

      console.log("Submit comment response status:", response.status)

      if (response.ok) {
        setNewComment("")
        await fetchTimelineData()
      } else {
        console.error("Failed to submit comment:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting comment:", error)
    }
  }

  const handleVote = async (suggestionId, voteType) => {
    console.log("handleVote called with:", suggestionId, voteType)

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/suggestions/${suggestionId}/vote`

      console.log("Submitting vote to:", url)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ voteType }),
      })

      console.log("Vote response status:", response.status)

      if (response.ok) {
        await fetchTimelineData()
      } else {
        console.error("Failed to submit vote:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting vote:", error)
    }
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Not set"
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString()
    } catch (e) {
      return "Invalid Date"
    }
  }

  useEffect(() => {
    console.log("useEffect triggered with actionId:", actionId)
    fetchTimelineData()
  }, [actionId])

  console.log("useTimeline hook returning state:", {
    timelineStartDate: timeline.startDate,
    timelineEndDate: timeline.endDate,
    milestonesCount: timeline.milestones?.length || 0,
    suggestionsCount: suggestions.length,
    commentsCount: comments.length,
    loading,
  })

  return {
    // State
    timeline,
    suggestions,
    comments,
    newSuggestion,
    newComment,
    activeTab,
    loading,

    // Actions
    setActiveTab,
    setNewComment,
    updateNewSuggestion,
    handleAddMilestone,
    handleRemoveMilestone,
    handleMilestoneChange,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote,
    formatDate,
    fetchTimelineData,
  }
}

export default useTimeline
